import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "../axios";
import { getTimeBasedQuote } from "../utils/quotes";

function AdminProfile() {
    const { user } = useAuth();
    const [monthlyData, setMonthlyData] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(
        new Date().getMonth() + 1
    );
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const months = [
        "januar",
        "februar",
        "marec",
        "april",
        "maj",
        "junij",
        "julij",
        "avgust",
        "september",
        "oktober",
        "november",
        "december",
    ];

    // Fetch data effect
    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            setError(null);
            try {
                const response = await axios.get(
                    `/api/admin/metrics/monthly/${selectedYear}`,
                    { withCredentials: true }
                );

                // Get current month's data
                const monthName = months[selectedMonth - 1];
                const monthData = response.data[monthName];

                if (Array.isArray(monthData)) {
                    // Try to find user data by email first, then by name
                    const currentUserData = monthData.find((item) => {
                        return (
                            (item.email &&
                                item.email.toLowerCase() ===
                                    user?.email?.toLowerCase()) ||
                            (item.name &&
                                item.name.toLowerCase() ===
                                    user?.name?.toLowerCase())
                        );
                    });

                    if (currentUserData) {
                        // Transform the data to match the expected structure
                        const transformedData = {
                            ...currentUserData,
                            nove: currentUserData.enote?.nove || 0,
                            preklicane: currentUserData.enote?.preklicane || 0,
                            dobljene: currentUserData.enote?.dobljene || 0,
                            lk: currentUserData.enote?.lk || 0,
                            skupaj: currentUserData.enote?.skupaj || 0,
                            percentage: currentUserData.enote?.percentage
                                ? currentUserData.enote.percentage > 1
                                    ? currentUserData.enote.percentage
                                    : currentUserData.enote.percentage * 100
                                : 0,
                            njs: currentUserData.enote?.njs || 0,
                            neZeli: currentUserData.enote?.neZeli || 0,
                            delamoZe: currentUserData.enote?.delamoZa || 0,
                            neaktivne: currentUserData.enote?.neaktivne || 0,

                            stVnesenih: currentUserData.stranke?.stVnesen || 0,
                            stStrank: currentUserData.stranke?.stStrank || 0,
                            stPonudb: currentUserData.stranke?.stPonudbe || 0,
                            krizanje: currentUserData.stranke?.krizanje || 0,
                            orgStranke:
                                currentUserData.stranke?.orgStranke || 0,
                            orgOgledi: currentUserData.stranke?.orgOgledi || 0,
                            silosi: currentUserData.stranke?.silosi || 0,
                            projekti: currentUserData.stranke?.projekti || 0,
                            preklicaneStranke:
                                currentUserData.stranke?.preklicane || 0,

                            preklicanePrevzemi:
                                currentUserData.prevzemi?.preklicane2 || 0,
                            realizirano:
                                currentUserData.prevzemi?.realizirano || 0,
                        };

                        setMonthlyData([transformedData]);
                        setError(null);
                    } else {
                        setMonthlyData([]);
                        setError(`Trenutno ni razpoložljivih podatkov.`);
                    }
                } else {
                    setMonthlyData([]);
                    setError(`Ni podatkov za mesec: ${monthName}`);
                }
            } catch (error) {
                setError("Prišlo je do napake.");
            } finally {
                setIsLoading(false);
            }
        }

        if (user) {
            fetchData();
        } else {
            setError("Ni podatkov o uporabniku.");
            setIsLoading(false);
        }
    }, [selectedYear, selectedMonth, user]);

    const formatNumber = (value) => {
        if (typeof value === "number") {
            return value.toFixed(1);
        }
        if (!isNaN(parseFloat(value))) {
            return parseFloat(value).toFixed(1);
        }
        return value;
    };

    const formatPercentage = (value) => {
        if (typeof value === "number") {
            // If the value is already in percentage form (e.g., 33), return as is
            if (value > 1) {
                return value.toFixed(1);
            }
            // If the value is in decimal form (e.g., 0.33), multiply by 100
            return (value * 100).toFixed(1);
        }
        if (!isNaN(parseFloat(value))) {
            const num = parseFloat(value);
            if (num > 1) {
                return num.toFixed(1);
            }
            return (num * 100).toFixed(1);
        }
        return value;
    };

    if (isLoading) {
        return (
            <div className="p-6 mx-auto max-w-7xl">
                <div className="flex gap-2 mb-6">
                    <select
                        disabled
                        className="px-4 py-2 border border-blue-300 rounded-md opacity-50"
                        style={{ minWidth: "120px" }}
                    >
                        <option>Januar</option>
                    </select>
                    <select
                        disabled
                        className="px-4 py-2 border border-gray-300 rounded-md opacity-50"
                        style={{ minWidth: "100px" }}
                    >
                        <option>2024</option>
                    </select>
                </div>
                <div className="py-8 text-center">
                    <div className="w-12 h-12 mx-auto border-b-2 border-blue-500 rounded-full animate-spin"></div>
                    <p className="mt-4 text-gray-600">Nalaganje podatkov...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 mx-auto max-w-7xl">
            {/* Month/Year Selection */}
            <div className="flex gap-2 mb-6">
                <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="px-4 py-2 text-gray-700 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ minWidth: "120px" }}
                >
                    {months.map((month, index) => (
                        <option key={month} value={index + 1}>
                            {month.charAt(0).toUpperCase() + month.slice(1)}
                        </option>
                    ))}
                </select>
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ minWidth: "100px" }}
                >
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                    <option value="2026">2026</option>
                </select>
            </div>

            {/* Metrics Section */}
            {monthlyData.length > 0 && monthlyData[0] && (
                <div className="space-y-8">
                    {/* Main Headline */}
                    <div className="text-center">
                        <h1 className="mb-2 text-3xl font-bold text-gray-800">
                            {getTimeBasedQuote(user?.name || "").text}
                        </h1>
                        <p className="text-gray-600">
                            Pregled vaših mesečnih statistik
                        </p>
                    </div>

                    {/* Averages Section */}
                    <div>
                        <div className="flex items-center mb-6">
                            <div className="flex-grow h-px bg-gray-200"></div>
                            <h2 className="px-4 text-xl font-semibold text-gray-700">
                                DNEVNA POVPREČJA
                            </h2>
                            <div className="flex-grow h-px bg-gray-200"></div>
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {/* ENOTE Card */}
                            <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                                <div className="px-6 py-4 border-b border-blue-100 bg-blue-50">
                                    <h2 className="text-xl font-bold text-blue-900">
                                        ENOTE
                                    </h2>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Nove
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].nove
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Preklicane
                                            </p>
                                            <p className="text-lg font-semibold text-red-600">
                                                {formatNumber(
                                                    monthlyData[0].preklicane
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Dobljene
                                            </p>
                                            <p className="text-lg font-semibold text-green-600">
                                                {formatNumber(
                                                    monthlyData[0].dobljene
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                LK
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].lk
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Skupaj
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].skupaj
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                %
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatPercentage(
                                                    monthlyData[0].percentage
                                                )}
                                                %
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                NJS
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].njs
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Ne želi
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].neZeli
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Delamo že
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].delamoZe
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Neaktivne
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].neaktivne
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* STRANKE Card */}
                            <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                                <div className="px-6 py-4 border-b border-green-100 bg-green-50">
                                    <h2 className="text-xl font-bold text-green-900">
                                        STRANKE
                                    </h2>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Število vnešenih
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].stVnesenih
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Št. strank
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].stStrank
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Št. Ponudbe
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].stPonudb
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Križanje
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].krizanje
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Org. Stranke
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].orgStranke
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Org. ogledi
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].orgOgledi
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Silosi
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].silosi
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Projekti
                                            </p>
                                            <p className="text-lg font-semibold">
                                                {formatNumber(
                                                    monthlyData[0].projekti
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Preklicane
                                            </p>
                                            <p className="text-lg font-semibold text-red-600">
                                                {formatNumber(
                                                    monthlyData[0]
                                                        .preklicaneStranke
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* PREVZEMI Card */}
                            <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                                <div className="px-6 py-4 border-b border-yellow-100 bg-yellow-50">
                                    <h2 className="text-xl font-bold text-yellow-900">
                                        PREVZEMI
                                    </h2>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Preklicane
                                            </p>
                                            <p className="text-lg font-semibold text-red-600">
                                                {formatNumber(
                                                    monthlyData[0]
                                                        .preklicanePrevzemi
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                Realizirano
                                            </p>
                                            <p className="text-lg font-semibold text-green-600">
                                                {formatNumber(
                                                    monthlyData[0].realizirano
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="p-4 mb-6 text-red-700 border border-red-200 rounded-lg bg-red-50">
                    <p>{error}</p>
                </div>
            )}
        </div>
    );
}

export default AdminProfile;
